/**
 * Change the operator's password (v6.1.0).
 *
 * Three fields — current, new, confirm — in the same bottom sheet the rest of
 * the app uses, so the focus trap, Escape and backdrop dismissal behave exactly
 * as they do everywhere else.
 *
 * WHAT IT PROMISES, in its own words, because the one thing a person needs to
 * know before tapping is the consequence: "You'll stay signed in here. Every
 * other device will be signed out." That is what the Worker does, and
 * test/worker/password-change.test.ts pins it.
 *
 * The fields are real <input type="password"> with the autocomplete tokens a
 * password manager keys on (`current-password`, `new-password`), inside a
 * <form> so Return submits. They are never logged, never stored, and the
 * values are not echoed anywhere — including the error message.
 */

import { trapFocus } from '../lib/dialog-trap';
import { NEW_PASSWORD_HINT, checkPasswordInput } from '../lib/password-errors';

export interface PasswordSheetOptions {
  /**
   * Send the change. Resolving means it worked and the caller will close the
   * sheet; rejecting leaves the sheet open with the error shown.
   */
  onSubmit: (currentPassword: string, newPassword: string) => Promise<void>;
  onDismiss: () => void;
  /** Turn a rejection into words. Must never echo what was typed. */
  describeError: (err: unknown) => string;
}

function field(
  id: string,
  label: string,
  autocomplete: 'current-password' | 'new-password',
): { label: HTMLLabelElement; input: HTMLInputElement } {
  const lbl = document.createElement('label');
  lbl.className = 'sheet-field-label';
  lbl.htmlFor = id;
  lbl.textContent = label;

  const input = document.createElement('input');
  input.className = 'sheet-input';
  input.type = 'password';
  input.id = id;
  input.autocomplete = autocomplete;
  // A password is not prose: no autocorrect, no capital on the first letter, no
  // dictionary underline. iOS applies all three to a text field by default.
  input.autocapitalize = 'none';
  input.spellcheck = false;
  input.setAttribute('autocorrect', 'off');
  return { label: lbl, input };
}

export function createPasswordSheet(opts: PasswordSheetOptions): HTMLElement {
  const backdrop = document.createElement('div');
  backdrop.className = 'sheet-backdrop';

  const sheet = document.createElement('div');
  sheet.className = 'sheet';
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-labelledby', 'password-sheet-title');

  const title = document.createElement('h2');
  title.id = 'password-sheet-title';
  title.className = 'sheet-title';
  title.textContent = 'Change your password';

  const sub = document.createElement('p');
  sub.className = 'sheet-sub';
  sub.textContent = "You'll stay signed in here. Every other device will be signed out.";

  const form = document.createElement('form');
  form.className = 'sheet-form';
  // No action and no autofill-driven navigation: submission is handled below.
  form.noValidate = true;

  const current = field('pw-current', 'Current password', 'current-password');
  const next = field('pw-new', 'New password', 'new-password');
  const confirmField = field('pw-confirm', 'New password again', 'new-password');

  const hint = document.createElement('p');
  hint.className = 'sheet-note soft';
  hint.id = 'pw-hint';
  hint.textContent = NEW_PASSWORD_HINT;
  next.input.setAttribute('aria-describedby', 'pw-hint');

  const message = document.createElement('p');
  message.className = 'sheet-message';
  message.setAttribute('role', 'alert');

  const actions = document.createElement('div');
  actions.className = 'sheet-actions';

  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn-quiet';
  cancel.textContent = 'Not now';
  cancel.addEventListener('click', () => opts.onDismiss());

  const confirm = document.createElement('button');
  confirm.type = 'submit';
  confirm.className = 'btn-primary';
  confirm.textContent = 'Change password';
  confirm.disabled = true;

  actions.append(cancel, confirm);

  const inputs = [current.input, next.input, confirmField.input];
  const refreshEnabled = () => {
    confirm.disabled = inputs.some((i) => i.value === '');
  };
  for (const input of inputs) {
    input.addEventListener('input', () => {
      message.textContent = '';
      refreshEnabled();
    });
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (confirm.disabled) return;

    // Cheap local checks first: they cost nothing, and a request that cannot
    // succeed would still spend one of the Worker's five attempts.
    const problem = checkPasswordInput(
      current.input.value,
      next.input.value,
      confirmField.input.value,
    );
    if (problem) {
      message.textContent = problem;
      return;
    }

    confirm.disabled = true;
    confirm.textContent = 'Changing…';
    try {
      await opts.onSubmit(current.input.value, next.input.value);
    } catch (err) {
      message.textContent = opts.describeError(err);
      confirm.textContent = 'Change password';
      refreshEnabled();
    }
  });

  form.append(
    current.label,
    current.input,
    next.label,
    next.input,
    hint,
    confirmField.label,
    confirmField.input,
    message,
    actions,
  );
  sheet.append(title, sub, form);
  backdrop.append(sheet);

  trapFocus({ backdrop, sheet, onDismiss: opts.onDismiss });

  queueMicrotask(() => current.input.focus());
  return backdrop;
}
